import {hasServerFlag, SERVER_FLAGS} from '../../../../../config';
import {type RunsLinePlotConfig} from '../../../../PanelRunsLinePlot/types';
import {
  type DerivedPointVisualizationOption,
  isPointVizFullFidelity,
  type LinePlotSettings,
} from '../../types';
import {
  DEFAULT_FULL_SCREEN_MAX_BUCKETED_RUNS,
  DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS,
  DEFAULT_LINE_PLOT_NUM_RUNS_SAMPLING,
  DEFAULT_OVERRIDE_MAX_BUCKETED_RUNS,
  DEFAULT_PANEL_MAX_NUM_RUNS,
} from '../maxRunDefaults';

/**
 * Resolve the effective max-runs value after cascading workspace -> section
 * -> panel settings. Takes the user's saved value (`limit` from panel config,
 * falling back to `maxRuns` from workspace settings) and clamps it to the
 * environment-aware ceiling from `getMaxRunsCeiling`. Returns `undefined`
 * when no setting has been saved, letting callers fall back to their own
 * defaults.
 *
 * Note: this is called via `getLinePlotSettingsWithDefaults`, which resolves
 * settings for multiple panel types (line plots, scalar charts, parallel
 * coordinates). It intentionally uses the generic panel ceiling (`isLinePlot`
 * defaults to false) so non-line-plot panels get the higher 300-run sampling
 * limit rather than the line-plot-specific 100.
 */
export const getCascadingMaxRuns = ({
  canOverrideMaxRuns,
  pointVisualizationMethod,
  maxRuns,
  limit,
  isFullScreen,
  canIncreaseFullScreenMaxRuns,
  dynamicConfigMaxRuns,
  dynamicConfigFullScreenMaxRuns,
}: {
  canOverrideMaxRuns?: boolean;
  pointVisualizationMethod: DerivedPointVisualizationOption;
  maxRuns: LinePlotSettings['maxRuns'];
  limit?: RunsLinePlotConfig['limit'];
  isFullScreen?: boolean;
  canIncreaseFullScreenMaxRuns?: boolean;
  dynamicConfigMaxRuns?: number;
  dynamicConfigFullScreenMaxRuns?: number;
}) => {
  const isBucketing = isPointVizFullFidelity(pointVisualizationMethod);

  const maxValue = getMaxRunsCeiling({
    canOverrideMaxRuns: canOverrideMaxRuns ?? false,
    isBucketing,
    isFullScreen,
    canIncreaseFullScreenMaxRuns,
    dynamicConfigMaxRuns,
    dynamicConfigFullScreenMaxRuns,
  });

  const passedLimit = limit ?? maxRuns;
  if (passedLimit == null) {
    return undefined;
  }

  // if both are undefined above than we'll have a value here even if TS can't pick it up
  return Math.min(maxValue, (limit ?? maxRuns) as number);
};

/**
 * Return the hard upper bound on the number of runs a user can configure
 * for a chart. This is a *policy* function — it doesn't read or modify the
 * user's saved setting; it only determines the ceiling that other functions
 * (getCascadingMaxRuns, clampMaxRunsForLinePlot) clamp against.
 *
 * For bucketing mode the ceiling is computed as a monotonically-increasing
 * accumulation — each source can only raise the ceiling, never lower it:
 *
 * 1. Server flag — if set, it is authoritative and returned immediately.
 * 2. Default — start with DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS (50).
 * 3. Each source can only raise the ceiling, never lower it:
 *    - `canOverrideMaxRuns` → DEFAULT_OVERRIDE_MAX_BUCKETED_RUNS
 *    - `dynamicConfigMaxRuns` — per-org Statsig value
 *    - When `isFullScreen`:
 *      - `canIncreaseFullScreenMaxRuns` → DEFAULT_FULL_SCREEN_MAX_BUCKETED_RUNS
 *      - `dynamicConfigFullScreenMaxRuns` — per-org Statsig value for full-screen
 *
 * Non-bucketing (sampling) mode bypasses all of the above and returns either
 * DEFAULT_LINE_PLOT_NUM_RUNS_SAMPLING (line plots) or DEFAULT_PANEL_MAX_NUM_RUNS (other panels).
 *
 */
export const getMaxRunsCeiling = ({
  canOverrideMaxRuns,
  isBucketing,
  isFullScreen = false,
  canIncreaseFullScreenMaxRuns = false,
  isLinePlot = false,
  dynamicConfigMaxRuns,
  dynamicConfigFullScreenMaxRuns,
}: {
  canOverrideMaxRuns: boolean;
  isBucketing: boolean;
  isFullScreen?: boolean;
  canIncreaseFullScreenMaxRuns?: boolean;
  isLinePlot?: boolean;
  dynamicConfigMaxRuns?: number;
  dynamicConfigFullScreenMaxRuns?: number;
}) => {
  // sampling limits are exempt from all bucketing overrides
  if (!isBucketing) {
    return isLinePlot
      ? DEFAULT_LINE_PLOT_NUM_RUNS_SAMPLING
      : DEFAULT_PANEL_MAX_NUM_RUNS;
  }

  // 1. Server flag is authoritative — if set, return it and skip everything else.
  //    DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS reads from serverFlagMaxRunsLimit()
  //    which has its own fallback default, so we check hasServerFlag to distinguish
  //    "server set a limit" from "using the hardcoded default".
  if (hasServerFlag(SERVER_FLAGS.maxRunsLimit)) {
    return DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS;
  }

  // 2. Start with the default, then let each source raise the ceiling.
  let ceiling = DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS;

  if (canOverrideMaxRuns) {
    ceiling = Math.max(ceiling, DEFAULT_OVERRIDE_MAX_BUCKETED_RUNS);
  }

  if (dynamicConfigMaxRuns != null) {
    ceiling = Math.max(ceiling, dynamicConfigMaxRuns);
  }

  // Full-screen sources — ramp flag and dynamic config
  if (isFullScreen) {
    if (canIncreaseFullScreenMaxRuns) {
      ceiling = Math.max(ceiling, DEFAULT_FULL_SCREEN_MAX_BUCKETED_RUNS);
    }
    if (dynamicConfigFullScreenMaxRuns != null) {
      ceiling = Math.max(ceiling, dynamicConfigFullScreenMaxRuns);
    }
  }

  return ceiling;
};

/**
 * Clamp a panel-level max-runs value to the line-plot-specific ceiling.
 * Used during settings inheritance (usePanelInheritedSettings) to ensure
 * that a panel's resolved `limit` never exceeds what the current
 * environment and feature flags allow for line plots.
 */
export const clampMaxRunsForLinePlot = (
  {
    isBucketing,
    canOverrideMaxRuns,
    isFullScreen,
    canIncreaseFullScreenMaxRuns,
    dynamicConfigMaxRuns,
    dynamicConfigFullScreenMaxRuns,
  }: {
    isBucketing: boolean;
    canOverrideMaxRuns: boolean;
    isFullScreen?: boolean;
    canIncreaseFullScreenMaxRuns?: boolean;
    dynamicConfigMaxRuns?: number;
    dynamicConfigFullScreenMaxRuns?: number;
  },
  limitFromPanelConfig?: number
) => {
  const maxRunLimit = getMaxRunsCeiling({
    canOverrideMaxRuns,
    isBucketing,
    isFullScreen,
    canIncreaseFullScreenMaxRuns,
    isLinePlot: true,
    dynamicConfigMaxRuns,
    dynamicConfigFullScreenMaxRuns,
  });
  return clampToLimit(limitFromPanelConfig, maxRunLimit);
};

/** Clamp a value to an upper bound, passing through `undefined` unchanged. */
export const clampToLimit = (value: number | undefined, limit: number) => {
  if (value) {
    return Math.min(value, limit);
  }
  return value;
};
